import './UploadSection.css';

import { useEffect, useRef, useState } from 'react';

const UploadSection = ({
  selectedImage,
  previewUrl,
  showCamera,
  stream,
  loading,
  isStreaming,
  streamingStatus,
  streamingProgress,
  onImageSelect,
  onStartCamera,
  onCaptureImage,
  onStopCamera,
  onUploadFromGallery,
  onConvert,
  onClearImage,
  videoRef,
  canvasRef,
}) => {
  const fileInputRef = useRef(null);
  const nativeCameraRef = useRef(null);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [cameraCapabilities, setCameraCapabilities] = useState(null);
  const [showCameraOptions, setShowCameraOptions] = useState(false);

  // Detect if user is on mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Get camera capabilities when camera starts
  useEffect(() => {
    if (stream && stream.getVideoTracks().length > 0) {
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      setCameraCapabilities(capabilities);
      console.log('Camera capabilities in component:', capabilities);
    }
  }, [stream]);

  const handleUploadFromGallery = () => {
    fileInputRef.current?.click();
  };

  const handleCameraAction = () => {
    // On mobile devices, directly open native camera
    if (isMobile) {
      nativeCameraRef.current?.click();
    } else {
      // On desktop, use web camera
      onStartCamera();
    }
  };

  const handleNativeCameraChange = event => {
    const file = event.target.files[0];
    if (file) {
      onImageSelect(event); // Use the existing image select handler
    }
  };

  const toggleTorch = async () => {
    if (!stream || !cameraCapabilities?.torch) return;

    try {
      const track = stream.getVideoTracks()[0];
      const newTorchState = !torchEnabled;

      await track.applyConstraints({
        advanced: [{ torch: newTorchState }],
      });

      setTorchEnabled(newTorchState);
      console.log('Torch toggled:', newTorchState);
    } catch (error) {
      console.error('Error toggling torch:', error);
    }
  };

  const focusAtPoint = async (x, y) => {
    if (!stream || !cameraCapabilities?.focusMode) return;

    try {
      const track = stream.getVideoTracks()[0];

      // Try to set manual focus with a specific point of interest
      await track.applyConstraints({
        advanced: [
          {
            focusMode: 'manual',
            pointsOfInterest: [{ x: x / videoRef.current.videoWidth, y: y / videoRef.current.videoHeight }],
          },
        ],
      });

      // Switch back to continuous autofocus after a moment
      setTimeout(async () => {
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: 'continuous' }],
          });
        } catch (err) {
          console.log('Could not return to continuous focus:', err);
        }
      }, 2000);

      console.log('Focus point set at:', x, y);
    } catch (error) {
      console.error('Error setting focus point:', error);
    }
  };

  const handleVideoClick = event => {
    if (!videoRef.current) return;

    const rect = videoRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Convert to video coordinates
    const videoX = (x / rect.width) * videoRef.current.videoWidth;
    const videoY = (y / rect.height) * videoRef.current.videoHeight;

    focusAtPoint(videoX, videoY);
  };

  return (
    <div className="upload-section">
      {!showCamera && (
        <>
          <div className="upload-options">
            <button onClick={handleCameraAction} className="camera-button">
              📸 Take Photo
            </button>

            <button onClick={handleUploadFromGallery} className="gallery-button">
              🖼️ Upload from Gallery
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onImageSelect}
            className="file-input"
            style={{ display: 'none' }}
          />

          {/* Native camera input */}
          <input
            ref={nativeCameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleNativeCameraChange}
            className="file-input"
            style={{ display: 'none' }}
          />
        </>
      )}

      {showCamera && (
        <div className="camera-section-fullscreen">
          <div className="camera-overlay">
            <div className="camera-controls-overlay">
              <button onClick={onStopCamera} className="camera-control-btn cancel-btn">
                ❌ Cancel
              </button>
              {cameraCapabilities?.torch && (
                <button
                  onClick={toggleTorch}
                  className={`camera-control-btn torch-btn ${torchEnabled ? 'torch-on' : ''}`}
                  title="Toggle flashlight"
                >
                  {torchEnabled ? '🔦' : '💡'} Flash
                </button>
              )}
              <button onClick={onCaptureImage} className="camera-control-btn capture-btn">
                📸 Capture
              </button>
            </div>
            <div className="camera-instructions">
              <p>Position your menu in the frame and tap capture</p>
              {cameraCapabilities?.focusMode && <p className="focus-instruction">💡 Tap anywhere to focus</p>}
            </div>
          </div>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video-fullscreen"
            onLoadedMetadata={() => console.log('Video metadata loaded')}
            onCanPlay={() => console.log('Video can play')}
            onPlay={() => console.log('Video started playing')}
            onError={e => console.error('Video error:', e)}
            onClick={handleVideoClick}
            style={{ cursor: cameraCapabilities?.focusMode ? 'crosshair' : 'default' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}

      {previewUrl && !showCamera && (
        <div className="preview-section">
          <div className="preview-header">
            <div className="preview-title-group">
              <h3 className="preview-title">Your Menu Preview</h3>
              <span className="preview-reminder">⚠️ Upload readable, good resolution menu</span>
            </div>
            <button onClick={onClearImage} className="cancel-preview-btn" title="Remove menu preview">
              ✕
            </button>
          </div>
          <img src={previewUrl} alt="Menu Preview" className="preview-image" />
        </div>
      )}

      {!showCamera && (
        <button onClick={onConvert} disabled={(!selectedImage && !previewUrl) || loading} className="convert-button">
          {loading ? (
            <>
              <span className="spinner"></span>
              Processing...
            </>
          ) : (
            <>Generate Menu!</>
          )}
        </button>
      )}
    </div>
  );
};

export default UploadSection;
